import React, { useState } from 'react';
import { Accordion } from '@noths/polaris-client-ribbons-design-system';
import { palette } from '@noths/polaris-design-system-theme-brand2026customer';

import { FacetHeader } from 'src/components/atoms/FacetHeader/FacetHeader';
import * as styles from 'src/components/organisms/ListingsControls/ListingsControls.styles';
import type { UIFacet } from 'src/components/types/UIFacet';
import type { UISortOption } from 'src/components/types/UISortOption';
import { resolveFacetOptionsComponent } from '../ListingsControlsOptions/resolveFacetOptionsComponent';
import { SortOptions } from '../ListingsControlsOptions/SortOptions';

export const SORT_ACCORDION_ID = 'sort-accordion';
const SORT_TITLE = 'Sort';

export interface ListingsControlsProps {
  facets: UIFacet[];
  onAllFacetsVisible: VoidFunction;
  sortOptions: UISortOption[];
}

export const ListingsControls = ({
  facets,
  onAllFacetsVisible,
  sortOptions,
}: ListingsControlsProps) => {
  const [openAccordionId, setOpenAccordionId] = useState<string | null>(null);

  const handleFacetVisibility = (facetKey: string) => {
    const facetIndex = facets.findIndex(({ key }) => key === facetKey);
    const lastFacetIndex = facetIndex === facets.length - 1;

    if (lastFacetIndex) {
      onAllFacetsVisible();
    }
  };

  return (
    <form css={[styles.listingsControls]} onSubmit={(e) => e.preventDefault()}>
      <div css={[styles.list]}>
        <hr css={styles.separator} />
        {sortOptions.length > 0 &&
          (() => {
            const sortOpen = openAccordionId === SORT_ACCORDION_ID;

            return (
              <div css={styles.listItem}>
                <Accordion
                  iconColor="var(--neutral-black-base)"
                  id={SORT_ACCORDION_ID}
                  onClick={(ev) => {
                    ev.preventDefault();
                    setOpenAccordionId(sortOpen ? null : SORT_ACCORDION_ID);
                  }}
                  open={sortOpen}
                  title={
                    <FacetHeader
                      heading={SORT_TITLE}
                      options={!sortOpen ? sortOptions : undefined}
                    />
                  }
                >
                  <div css={styles.facetOptionsAccordionWrapper}>
                    <SortOptions sortOptions={sortOptions} />
                  </div>
                </Accordion>
              </div>
            );
          })()}
        {facets.map((facet) => {
          if (facet.options.length === 0) {
            return null;
          }

          const activeOptions = facet.options.filter((option) => option.active);
          const hasActiveOptions = activeOptions.length > 0;
          const facetOpen = openAccordionId === facet.key;
          const FacetOptionsComponent = resolveFacetOptionsComponent(facet);

          return (
            <React.Fragment key={facet.key}>
              <hr css={styles.separator} />
              <div css={styles.listItem}>
                {facet.options.length > 1 || facet.singleOptionOnly !== true ? (
                  <Accordion
                    iconColor={hasActiveOptions ? palette.greyBase : 'currentColor'}
                    id={facet.key}
                    onClick={(ev) => {
                      ev.preventDefault();
                      setOpenAccordionId(facetOpen ? null : facet.key);
                    }}
                    onVisible={() => handleFacetVisibility(facet.key)}
                    open={facetOpen}
                    title={
                      <FacetHeader
                        heading={facet.title}
                        options={!facetOpen ? facet.options : undefined}
                      />
                    }
                  >
                    <div css={styles.facetOptionsAccordionWrapper}>
                      <FacetOptionsComponent facet={facet} />
                    </div>
                  </Accordion>
                ) : (
                  <div
                    css={styles.facetOptionsWrapper}
                    style={
                      {
                        '--borderColor': hasActiveOptions ? palette.greyBase : 'transparent',
                      } as styles.BorderCSS
                    }
                  >
                    <FacetOptionsComponent boldText={true} facet={facet} />
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}
        <hr css={styles.separator} />
      </div>
    </form>
  );
};
